import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Zap, Lock, ShieldCheck, Star, Library } from 'lucide-react';
import { getBookById, books } from '../data/books';
import SubscribeForm from './SubscribeForm';
import './BookPreview.css';

const BookPreview = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);

  useEffect(() => {
    const found = getBookById(bookId);
    if (found) {
      setBook(found);
      window.scrollTo(0, 0);
    } else {
      navigate('/');
    }
  }, [bookId, navigate]);

  if (!book) {
    return (
      <div className="op-loading">
        <div className="op-spinner" aria-label="Loading" />
      </div>
    );
  }

  return (
    <div className="optin">
      <div className="optin-glow" aria-hidden="true" />
      <div className="container optin-container">
        <button className="back-btn" onClick={() => navigate('/')}>
          <ArrowLeft size={18} />
          <span>Back to library</span>
        </button>

        <div className="optin-grid">
          {/* ---------- Value panel ---------- */}
          <motion.aside
            className="optin-value"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="value-book">
              <div className="value-cover">
                <img src={book.coverImage} alt={`${book.title} cover`} />
              </div>
              <div className="value-book-info">
                <span className="value-category">{book.category}</span>
                <h1>{book.title}</h1>
                <p className="value-subtitle">{book.subtitle}</p>
                <div className="value-meta">
                  <span>{book.pages} pages</span>
                  <span className="dot" />
                  <span>{book.readTime} read</span>
                  <span className="dot" />
                  <span>{book.difficulty}</span>
                </div>
              </div>
            </div>

            <div className="value-inside">
              <h2>What’s inside</h2>
              <ul>
                {book.highlights.map((h) => (
                  <li key={h}>
                    <Check size={16} strokeWidth={2.5} />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="value-bonus">
              <Library size={20} />
              <p>
                <strong>Plus all 5 books.</strong> Your access includes the complete AI Wealth
                Library — over 1,250 pages of strategy, free.
              </p>
            </div>

            <figure className="value-quote">
              <div className="value-stars" aria-hidden="true">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
                ))}
              </div>
              <blockquote>
                “I landed my first paying client the same week using tools I never knew existed.”
              </blockquote>
              <figcaption>Chidinma O. — Lagos</figcaption>
            </figure>
          </motion.aside>

          {/* ---------- Form ---------- */}
          <motion.div
            className="optin-form-col"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <SubscribeForm book={book} totalBooks={books.length} />

            <div className="trust-row">
              <span><Zap size={15} /> Instant access</span>
              <span><Lock size={15} /> 100% free</span>
              <span><ShieldCheck size={15} /> No spam</span>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BookPreview;
